// Helper function to extract text content from HTML and count words
export const getWordCountFromHtml = (html: string): number => {
    if (!html) return 0;
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    const text = tempDiv.textContent || tempDiv.innerText || "";
    const words = text.trim().split(/\s+/).filter(w => w.length > 0);
    return words.length;
};

/**
 * Recursively find a pool in the hierarchical licenses data by pool UUID
 */
export const findPoolInHierarchy = (data: any, targetPoolId: string): any => {
    if (!data) return null;

    // Check if this is a pool object
    if (data.serialNumber) {
        const poolId = typeof data.serialNumber === 'string' ? data.serialNumber : data.serialNumber?.poolId;
        if (poolId === targetPoolId) {
            return data;
        }
    }

    // Recursively search in licenses array
    if (data.licenses && Array.isArray(data.licenses)) {
        for (const license of data.licenses) {
            const found = findPoolInHierarchy(license, targetPoolId);
            if (found) return found;
        }
    }

    // Recursively search in accounts array
    if (data.accounts && Array.isArray(data.accounts)) {
        for (const account of data.accounts) {
            const found = findPoolInHierarchy(account, targetPoolId);
            if (found) return found;
        }
    }

    return null;
};

/**
 * Check if an account is a direct child of the login account
 * Returns true if selectedAccountId is found in loginData.accounts (direct child)
 * Returns false if found deeper in hierarchy (grandchild+) or not found
 */
export const isDirectChildOfLoginAccount = (
    loginData: any,
    selectedAccountId: string | null
): boolean => {
    if (!loginData || !selectedAccountId) return true; // Default to true if no data

    // Check if selectedAccountId exists in direct accounts array
    if (loginData.accounts && Array.isArray(loginData.accounts)) {
        const directChild = loginData.accounts.find(
            (account: any) => account.accountId === selectedAccountId
        );

        if (directChild) {
            return true; // Found as direct child
        }
    }

    return false; // Either not found, or found deeper in hierarchy (grandchild+)
};

/**
 * Check if a pool is owned by the selected account (parentPool: null in that account's context)
 * A parent cannot reassign pools owned by their children
 * Returns true if pool is owned by selected account (should disable), false otherwise
 */
export const isPoolOwnedBySelectedAccount = (
    data: any,
    selectedAccountId: string | null,
    poolId: string,
): boolean => {
    if (!data || !selectedAccountId || !poolId) return false;

    // Find the selected account in the hierarchy
    const findAccountById = (searchData: any, accountId: string): any => {
        if (searchData.accountId === accountId) {
            return searchData;
        }

        if (searchData.accounts && Array.isArray(searchData.accounts)) {
            for (const account of searchData.accounts) {
                const found = findAccountById(account, accountId);
                if (found) return found;
            }
        }

        return null;
    };

    const selectedAccount = findAccountById(data, selectedAccountId);
    if (!selectedAccount) return false;

    // Check if the pool exists in the selected account's licenses with parentPool: null
    if (selectedAccount.licenses && Array.isArray(selectedAccount.licenses)) {
        for (const license of selectedAccount.licenses) {
            const licensePoolId = typeof license.serialNumber === 'string'
                ? license.serialNumber
                : license.serialNumber?.poolId;

            // Pool is owned by selected account if it exists there with parentPool: null
            if (licensePoolId === poolId && license.parentPool === null) {
                return true; // Pool is owned by selected account - cannot reassign
            }
        }
    }

    return false; // Pool is not owned by selected account
};

/**
 * Get the email identifier from a LicenseManager object
 * Backend now sends 'id' (email) instead of 'email' field for security
 * This helper provides backwards compatibility with existing data
 */
export const getManagerEmail = (manager: { id?: string; email?: string } | null | undefined): string => {
    if (!manager) return '';
    return manager.id || manager.email || '';
};